/* BLUE SALON — Gender-scoped predictive search tabs.

   Sobooster's predictive/instant search box (the header search icon's
   dropdown, class .usf-popup.usf-is — a separate Vue component from the
   PLP grid's .usf-sr-container, with its own facetFilters state that
   usf.queryRewriter does NOT reach; confirmed live, setting queryRewriter
   .facetFilters fired no request and left the search panel's results
   unchanged) has no built-in "search within Women/Men/Kids" scoping and
   Sobooster's own settings/docs expose no such option (checked
   window.usf.settings and docs.sobooster.com directly).

   The one thing that IS real and working is the facetFilters query param
   itself — reverse-engineered from the site's own Gender sidebar filter,
   confirmed via its actual live requests:
     facetFilters={"237954416":["tags",["Womens","Unisex"]]}   (Women tab)
     facetFilters={"237954416":["tags",["Unisex","Mens"]]}     (Men tab)
     facetFilters={"237954416":["tags",["Boys","Girls"]]}      (Kids tab)

   So this adds the tab UI ourselves and rewrites the outgoing request at
   the network layer — the only place both components' requests are
   reachable, and the only approach that doesn't require patching
   Sobooster's own (vendor-synced, easily overwritten by an app update)
   usf.js. Gated on `.usf-popup.usf-is` existing in the DOM specifically —
   that class only ever belongs to the search widget, so requests from
   ordinary PLP grid browsing (.usf-sr-container, a different component)
   are never touched even though they hit the identical /search endpoint.

   The actual request goes out via XMLHttpRequest (Sobooster's Vue app
   uses Axios' default XHR adapter, not fetch) — confirmed live: patching
   window.fetch and clicking the real Gender sidebar checkbox produced
   zero fetch calls, while patching XMLHttpRequest.prototype.open caught
   the exact request. Both are patched below regardless; the fetch patch
   is a near-zero-cost guard in case that ever changes.

   Making a tab switch actually REFETCH turned out to be its own problem,
   solved only by finding window.usf.instantSearch — a live reference to
   the popup's own Vue component instance (exposed as a plain global, not
   attached to the DOM node, which is why __vue__/__vueParentComponent on
   the element itself found nothing). Once found, its methods were tried
   in order of increasing directness:
     - Dispatching synthetic DOM "input" events (even genuine InputEvents
       with real value changes) never refetches if the resulting text
       matches one Sobooster already fetched for — confirmed by setting
       comp.term directly and reading it back unchanged afterward, and by
       a debug hook inside the XHR patch itself (the only vantage point
       immune to the pre/post-rewrite ambiguity every other spy attempt
       ran into). A "nudge to a throwaway value, then restore" pattern
       fails for exactly this reason: the restore half reproduces a
       string already on record.
     - Appending invisible characters (zero-width space) to force a
       string Sobooster has never seen: works for *triggering* a request,
       but silently corrupts result relevance — confirmed live, "shirt" +
       one invisible char under the Men scope returned zero results for a
       query that plainly has matches (Bugatti Gents Polo Shirt, verified
       moments earlier with the same scope and clean text). Unacceptable:
       a feature that can silently zero out real results is worse than
       the bug it's fixing.
     - comp.search(term) — the wrong lever entirely: this is the "press
       Enter to see the full page" method (navigates to /search).
     - comp.updateResults(true) — looked promising once (a store term
       genuinely differed from what was cached) but re-tested consecutive
       calls with an unchanged term and got nothing the second time.
       Same dedup, just one layer up.
     - window.usf.fetch(component, appendMode, silent) — the actual
       request-issuing primitive underneath all of the above, confirmed
       by reading its own source (it directly manipulates
       component.result, component.loader, and raises the "sr_dataReceived"
       event). Calling it directly with the term completely unchanged
       still fires a real request every single time, with correct,
       uncorrupted, properly-scoped results — 27 items, every one
       genuinely Women's, off nothing but a scope switch and the exact
       text already on screen. This is the one actually used below. */
(function () {
  "use strict";

  function storefrontUrl(path) {
    if (window.BS && typeof window.BS.storefrontUrl === "function") return window.BS.storefrontUrl(path);
    var shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (shopRoot || "/") + String(path || "").replace(/^\//, "");
  }

  var GENDER_FACET_ID = "237954416";
  var SCOPE_TAGS = {
    women: ["Womens", "Unisex"],
    men: ["Unisex", "Mens"],
    kids: ["Boys", "Girls"]
  };
  // Labels come from locales/*.json via snippets/storefront-i18n.liquid, which
  // renders an inline script ahead of this deferred one, so BS_I18N is already
  // populated by the time this module body runs. The English literals stay as
  // fallbacks for the case where that snippet is absent from a layout.
  var I18N = window.BS_I18N || {};
  var TABS = [
    { key: "all", label: I18N.all || "All" },
    { key: "women", label: I18N.women || "Women" },
    { key: "men", label: I18N.men || "Men" },
    { key: "kids", label: I18N.kids || "Kids" }
  ];
  var STORAGE_KEY = "bsSearchScope";

  // "View all results" (and pressing Enter) leaves the dropdown entirely and
  // does a real page navigation to /search, so the scope cannot ride along
  // on an XHR the way it does for the live preview — it has to be encoded
  // into the destination URL. Sobooster's own on-page format for that was
  // read straight out of the address bar after applying the Gender filter
  // through its sidebar: uff_3xo6sw_tags=Womens_usf_Unisex — values joined
  // by "_usf_". The "3xo6sw" segment is Sobooster's own short code for the
  // Gender facet, stable across all three scopes (verified for women, men
  // and kids), which is why it is written literally here; if the facet is
  // ever rebuilt in their admin this is the string to re-check.
  var SCOPE_URL_PARAM = "uff_3xo6sw_tags";
  var SCOPE_URL_VALUES = {
    women: "Womens_usf_Unisex",
    men: "Unisex_usf_Mens",
    kids: "Boys_usf_Girls"
  };

  // sessionStorage is the single source of truth for the selected scope,
  // deliberately re-read on every use rather than cached in a module
  // variable. Reported bug this addresses: pick Women, then Men, then close
  // and reopen the dropdown — the bar comes back showing Women (the
  // PREVIOUS choice) instead of Men, and the applied facet reverts with it.
  // Reproduced once; not reproducible on demand, and a setItem hook proved
  // no rogue writer (every write traced back to a genuine button click), so
  // the surviving explanation is a stale read of the scope rather than a
  // bad write — a second execution of this file, a bfcache restore, or any
  // re-init would each leave a module-level `activeScope` frozen at
  // whatever was stored when THAT copy initialised, then rebuild the bar
  // from it. Reading storage at the point of use makes every one of those
  // cases self-correcting: whatever the user picked last always wins,
  // because that is exactly what is in storage.
  var memoryScope = "all";

  function getScope() {
    try {
      var stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && (stored === "all" || SCOPE_TAGS[stored])) return stored;
    } catch (e) {
      // sessionStorage unavailable (private mode, etc.) — fall through.
    }
    return memoryScope;
  }

  function setScope(key) {
    memoryScope = key;
    try {
      sessionStorage.setItem(STORAGE_KEY, key);
    } catch (e) {
      // ignore — scope just won't persist across reloads this session
    }
  }

  memoryScope = getScope();

  // The PLP grid's own Gender filter hits /search; this predictive-search
  // popup hits /instantsearch — confirmed live via an XHR spy (this file's
  // very first version only matched /search and silently caught nothing
  // here, since it never once fired). Both take the same facetFilters
  // param shape, so one matcher covers both.
  function isSearchRequestUrl(urlStr) {
    return /-usf\.hotyon\.com\/(?:instant)?search(?:\?|$)/.test(urlStr);
  }

  function searchWidgetIsPresent() {
    return !!document.querySelector(".usf-popup.usf-is");
  }

  function withScopedFacetFilters(urlStr) {
    var url;
    try {
      url = new URL(urlStr, window.location.href);
    } catch (e) {
      return urlStr;
    }
    var tags = SCOPE_TAGS[getScope()];
    var existing = {};
    var raw = url.searchParams.get("facetFilters");
    if (raw) {
      try {
        existing = JSON.parse(raw) || {};
      } catch (e2) {
        existing = {};
      }
    }
    if (tags) {
      existing[GENDER_FACET_ID] = ["tags", tags];
    } else {
      delete existing[GENDER_FACET_ID];
    }
    if (Object.keys(existing).length) {
      url.searchParams.set("facetFilters", JSON.stringify(existing));
    } else {
      url.searchParams.delete("facetFilters");
    }
    return url.toString();
  }

  // Sobooster's actual search calls go out via XMLHttpRequest (Axios'
  // default adapter), not fetch — confirmed live: patching window.fetch and
  // clicking the real Gender sidebar checkbox produced zero fetch calls,
  // while patching XMLHttpRequest.prototype.open caught the exact request
  // instantly. The fetch patch below is kept anyway, at near-zero cost, in
  // case a future Sobooster version (or a different code path) switches
  // adapters — XHR is the one that matters today.
  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      try {
        var isRequestObj = typeof Request !== "undefined" && input instanceof Request;
        var urlStr = isRequestObj ? input.url : String(input);
        if (isSearchRequestUrl(urlStr) && searchWidgetIsPresent()) {
          var scoped = withScopedFacetFilters(urlStr);
          if (scoped !== urlStr) {
            input = isRequestObj ? new Request(scoped, input) : scoped;
          }
        }
      } catch (err) {
        if (window.console && console.error) console.error("Search scope: fetch patch failed", err);
      }
      return nativeFetch.call(this, input, init);
    };
  }

  // Reported bug: select Women, search "dress" (correct), select Men
  // (correct, single Zilli result), then just click back into the search
  // box — results silently revert to Women's, even though Men is still
  // shown as the active tab. Not the remount case above (the tab bar never
  // left the DOM here). This is a plain out-of-order network race:
  // clicking Men fires its own request, but the earlier Women request can
  // still be in flight and — if it happens to resolve second — its
  // response is what Sobooster's own fetch primitive writes into
  // component.result last, regardless of which request went out more
  // recently. Sobooster exposes no cancel token, but these are ordinary
  // XMLHttpRequests and we already wrap open() — so we can hold a
  // reference to every search request still in flight and abort the lot
  // the moment the scope changes. An aborted request never fires `load`,
  // so Sobooster's own handler never runs and a stale result can never be
  // written in the first place. That is a real fix rather than the repair
  // this used to do (notice-after-the-fact, then refetch), which left the
  // wrong products on screen in the meantime and could itself lose a race.
  // The after-the-fact check is kept below as a cheap backstop for any
  // request that slips through (e.g. one already past `load` dispatch).
  //
  // Locally none of this reproduces: over localhost these requests resolve
  // in a few hundred ms with nothing to contend with. Over the real site,
  // with genuine latency, overlapping responses are routine — which is why
  // the ordering has to be handled by construction rather than by trying
  // to out-time it.
  var inFlight = [];
  var requestCount = 0;

  /* ── Stale repaint guard ────────────────────────────────────────────
     The remaining reported symptom is the tab staying correct (Men) while
     the products revert to an earlier scope's (Women/Kids). Measured
     directly: at the moment the products change back, the request counter
     does not move — ZERO network requests fire. So this is not a race
     between responses at all; Sobooster is repainting from its own
     internal per-term cache, which still holds whatever IT last fetched.
     Our scope switch calls usf.fetch directly, which updates the visible
     result but evidently does not replace that cached entry, so any later
     repaint (refocusing the box, reopening the panel) resurrects the old
     products underneath a tab that still says Men.

     Rather than keep guessing which interaction triggers the repaint —
     several plausible ones could not be reproduced over localhost, where
     everything is fast and the panel is never torn down — this watches for
     the signature itself: the product list changed, but no request was
     issued to justify it. That can only be a repaint from stale state, so
     re-issue the correctly scoped fetch. Self-correcting whatever the
     cause, and it cannot loop: our own corrective fetch bumps
     requestCount, so the render it produces is attributed to a request and
     is left alone. ─────────────────────────────────────────────────── */
  var lastResultSig = null;
  var lastSeenRequestCount = 0;
  var correctionPending = false;

  function resultSignature() {
    var items = document.querySelectorAll(".usf-is-content-container .usf-title.usf-btn");
    var out = [];
    for (var i = 0; i < items.length && i < 3; i++) out.push(items[i].textContent.trim());
    return out.join("|");
  }

  function guardAgainstStaleRepaint() {
    if (!searchWidgetIsPresent()) return;
    var sig = resultSignature();
    if (sig === lastResultSig) return;

    var changedWithoutRequest = requestCount === lastSeenRequestCount;
    var hadPreviousRender = lastResultSig !== null && lastResultSig !== "";
    lastResultSig = sig;
    lastSeenRequestCount = requestCount;

    if (!sig || !hadPreviousRender) return;
    if (getScope() === "all") return;
    if (!changedWithoutRequest || correctionPending) return;

    correctionPending = true;
    setTimeout(function () {
      correctionPending = false;
      retriggerSearch();
    }, 50);
  }

  function abortInFlightSearches() {
    var pending = inFlight.slice();
    inFlight.length = 0;
    pending.forEach(function (xhr) {
      try {
        xhr.abort();
      } catch (e) {
        // already finished/aborted — nothing to do
      }
    });
  }

  var nativeXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    var requestScope = getScope();
    var isScopedSearch = false;
    try {
      if (typeof url === "string" && isSearchRequestUrl(url) && searchWidgetIsPresent()) {
        url = withScopedFacetFilters(url);
        isScopedSearch = true;
      }
    } catch (err) {
      if (window.console && console.error) console.error("Search scope: XHR patch failed", err);
    }
    if (isScopedSearch) {
      var self = this;
      requestCount += 1;
      inFlight.push(self);
      this.addEventListener("loadend", function () {
        var i = inFlight.indexOf(self);
        if (i !== -1) inFlight.splice(i, 1);
      });
      this.addEventListener("load", function () {
        if (getScope() !== requestScope) retriggerSearch();
      });
    }
    var rest = Array.prototype.slice.call(arguments, 2);
    return nativeXhrOpen.apply(this, [method, url].concat(rest));
  };

  function setActiveScope(key, bar) {
    if (getScope() === key) return;
    setScope(key);
    syncActivePill(bar);
    // Kill anything still in flight for the previous scope BEFORE issuing
    // the new request, so no older response can land on top of this one.
    abortInFlightSearches();
    retriggerSearch();
  }

  // Highlights whichever pill matches the stored scope. Split out so the
  // re-injection path can call it too: a bar built from a stale value is
  // exactly the reported "reverts to the previously selected tab" symptom,
  // so every bar gets re-synced against storage right after insertion.
  function syncActivePill(bar) {
    var scope = getScope();
    bar.querySelectorAll("[data-bs-scope]").forEach(function (btn) {
      btn.classList.toggle("is-active", btn.dataset.bsScope === scope);
    });
  }

  // See the file header for the full trail of what was tried before this —
  // window.usf.fetch is the actual request-issuing primitive, immune to
  // whatever "did the term actually change" gate lives above it in
  // updateResults/onSearchBoxInput. Calling it directly refetches every
  // time regardless of whether the visible query text changed, with
  // correct, uncorrupted results, which is exactly what a scope-only
  // change (same text, different facet) needs.
  function retriggerSearch() {
    var comp = window.usf && window.usf.instantSearch;
    var doFetch = window.usf && window.usf.fetch;
    if (!comp || !doFetch) return;
    doFetch(comp, 0, false);
  }

  function buildTabBar() {
    var bar = document.createElement("div");
    bar.className = "bs-search-scope-tabs";
    bar.setAttribute("role", "tablist");
    var scope = getScope();
    TABS.forEach(function (t) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.setAttribute("role", "tab");
      btn.className = "bs-search-scope-tab" + (t.key === scope ? " is-active" : "");
      btn.dataset.bsScope = t.key;
      btn.textContent = t.label;
      btn.addEventListener("click", function () {
        setActiveScope(t.key, bar);
      });
      bar.appendChild(btn);
    });
    return bar;
  }

  // The dropdown's own content container is rebuilt by Vue on every
  // keystroke/result update, so the tab bar has to be re-inserted (not just
  // inserted once) whenever it's missing — this runs on every relevant DOM
  // mutation, and bails out in one line whenever it's already there.
  //
  // Reported bug this also fixes: type a query, pick e.g. Kids (correct,
  // scoped results show), close the dropdown, reopen it — the Kids pill
  // still shows active, but the results are back to unscoped/all. Cause:
  // closing and reopening throws away Sobooster's popup and remounts a
  // fresh one, which runs ITS OWN default fetch for the current term with
  // no facet applied at all — our activeScope never left "kids", so the
  // tab bar re-renders with the right pill highlighted, but nothing had
  // told the new popup instance to actually refetch under that scope.
  // Re-running retriggerSearch() every time a tab bar is freshly inserted
  // (not just on click) keeps the two in sync. Delayed one tick so it
  // resolves after Sobooster's own just-mounted fetch, not racing it —
  // otherwise whichever response lands second wins regardless of which
  // one is actually correct.
  function ensureTabsInjected() {
    document.querySelectorAll(".usf-popup.usf-is").forEach(function (popup) {
      var existing = popup.querySelector(".bs-search-scope-tabs");
      if (existing) {
        // Already present, but re-assert the highlight against storage:
        // cheap, idempotent, and the last line of defence against a bar
        // left showing a scope the user has since moved off.
        syncActivePill(existing);
        return;
      }
      var target = popup.querySelector(".usf-is-content-container") || popup.querySelector(".usf-is--full__content");
      if (!target) return;
      target.insertBefore(buildTabBar(), target.firstChild);
      if (getScope() !== "all") {
        setTimeout(retriggerSearch, 60);
      }
    });
  }

  /* ── Carry the scope into the full results page ──────────────────────
     Leaving the dropdown ("view all results", or Enter in the search box)
     navigates to /search, where the grid is a different component reading
     its filters from the URL — so the scope has to be handed over as a
     query param rather than applied to a request. Both entry points are
     intercepted in the capture phase: Sobooster binds its own handler to
     these elements, and stopping propagation before it runs is the only
     way to substitute our own destination without patching their bundle. */
  function scopedSearchUrl(term) {
    var url = new URL(storefrontUrl("search"), window.location.origin);
    if (term) url.searchParams.set("q", term);
    var value = SCOPE_URL_VALUES[getScope()];
    if (value) url.searchParams.set(SCOPE_URL_PARAM, value);
    return url.toString();
  }

  function currentTerm() {
    var input = document.querySelector('input[name="q"]');
    return input ? input.value.trim() : "";
  }

  function goToScopedResults(e) {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = scopedSearchUrl(currentTerm());
  }

  document.addEventListener(
    "click",
    function (e) {
      if (!SCOPE_URL_VALUES[getScope()]) return;
      if (!e.target || !e.target.closest) return;
      var viewAll = e.target.closest(".usf-is-viewall, .usf-predictive-search__viewall");
      if (!viewAll || !viewAll.closest(".usf-popup.usf-is")) return;
      goToScopedResults(e);
    },
    true
  );

  document.addEventListener(
    "submit",
    function (e) {
      if (!SCOPE_URL_VALUES[getScope()]) return;
      var form = e.target;
      if (!form || !form.querySelector || !form.querySelector('input[name="q"]')) return;
      goToScopedResults(e);
    },
    true
  );

  /* ── "View all results" label ────────────────────────────────────────
     With a scope active that link does not lead to "all results" — it leads
     to results within Women/Men/Kids — so the word "all" is dropped and the
     scope named instead: "view Women results for cloth". Sobooster's own
     wording is left untouched on All, where it is already accurate.

     The whole label is rewritten rather than having a suffix appended,
     because the goal is to remove a word from the middle of their string,
     not add one at the end. Their original markup is stashed on the node
     first so switching back to All can restore it verbatim; they also
     re-render this label on every keystroke, which restores it anyway, so
     this runs from the mutation pass and no-ops whenever the text already
     reads correctly — that comparison is also what stops our own write
     from retriggering the observer indefinitely. */
  var SCOPE_LABELS = {
    women: I18N.women || "Women",
    men: I18N.men || "Men",
    kids: I18N.kids || "Kids"
  };

  // Sentence templates rather than concatenation: Arabic puts the scope and
  // the term in a different order from English, so the word order has to be
  // the translator's to choose. Placeholders are the ones storefront-i18n
  // renders. The template itself is theme-authored locale content and is
  // inserted as markup; only the scope and term interpolated into it are
  // escaped, since those two are the untrusted parts.
  var VIEW_SCOPE = I18N.view_scope || "view __SCOPE__ results";
  var VIEW_SCOPE_TERM = I18N.view_scope_term || "view __SCOPE__ results for __TERM__";

  function fillTemplate(tpl, scope, termHtml) {
    return tpl
      .replace("__SCOPE__", function () { return scope; })
      .replace("__TERM__", function () { return termHtml; });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function decorateViewAll() {
    var label = SCOPE_LABELS[getScope()];
    var term = currentTerm();
    document
      .querySelectorAll(".usf-popup.usf-is .usf-is-viewall, .usf-popup.usf-is .usf-predictive-search__viewall")
      .forEach(function (el) {
        var target = el.querySelector("button, a") || el;

        if (!label) {
          if (target.dataset.bsOriginalLabel) {
            target.innerHTML = target.dataset.bsOriginalLabel;
            delete target.dataset.bsOriginalLabel;
            delete target.dataset.bsWritten;
          }
          return;
        }

        var desired = term
          ? fillTemplate(VIEW_SCOPE_TERM, escapeHtml(label), "<b>" + escapeHtml(term) + "</b>")
          : fillTemplate(VIEW_SCOPE, escapeHtml(label), "");
        if (target.innerHTML === desired) return;

        // Only stash when the node holds Sobooster's OWN markup, identified
        // by it not matching the last thing we wrote here. Two traps, both
        // hit while building this: stashing only on first sight leaves the
        // stash stale once the search term changes (restoring a label with
        // the wrong term, or none), while stashing unconditionally captures
        // our own text when switching straight from one scope to another —
        // Women to Kids then back to All would "restore" to Women. Keeping
        // the stash to their markup also keeps the restore translated,
        // which rebuilding the sentence ourselves would not (EN and AR).
        if (target.innerHTML !== target.dataset.bsWritten) {
          target.dataset.bsOriginalLabel = target.innerHTML;
        }
        target.innerHTML = desired;
        target.dataset.bsWritten = desired;
      });
  }

  /* ── Anchor the panel to the field it belongs to ─────────────────────
     Sobooster positions the popup with an inline `left` against the body
     (its offsetParent is body, not the form), and the number it picks is
     tied to neither edge of anything on the page. Measured live on
     /search?q=gucci at a 1338px viewport: panel 334 → 1192, field 686 →
     1306, page container 24 → 1299. So it lines up with the field on
     neither side and with the container on neither side — it reads as
     floating, which is the single biggest reason the dropdown looks
     unanchored next to an otherwise gridded page.

     Fixed here rather than in CSS because the value we are overriding is an
     inline style recomputed on every open and resize, so a stylesheet rule
     would need !important and would still be fighting a moving number. In
     JS the correct edge can simply be measured.

     Which edge to match is decided by where the field actually sits, not by
     a hardcoded side: the header's field is at the start of its row while
     the search page's is at the end, and both flip under RTL. Matching the
     nearer page edge in each case is what makes the panel read as belonging
     to the field. The result is then clamped inside the container gutter so
     a wide panel under a field near the viewport edge cannot hang off it. */
  var PANEL_GUTTER = 24; // matches the product grid's own inset

  /* The anchor has to be the field the shopper actually opened the panel
     from, and it has to STAY that field once focus moves elsewhere.

     Deriving it from document.activeElement plus a querySelector fallback was
     a bug: the search page renders two q inputs (the header's and the band's),
     clicking a scope tab moves focus onto the tab button, and the fallback
     then returned whichever input comes first in the document — the header's,
     which sits at the far left of the page. So the panel sat correctly under
     the band's field until any tab was clicked, then jumped to the left edge
     and stayed there. Measured: panel x went 669 → 24 while its field stayed
     at 669, and it looked like All/Women/Men/Kids each had their own layout.

     Remembering the last focused input instead makes the anchor survive
     everything that steals focus — tab clicks, the results re-render, a
     click into the panel itself. */
  var lastSearchInput = null;

  document.addEventListener(
    "focusin",
    function (e) {
      var el = e.target;
      if (el && el.name === "q") lastSearchInput = el;
    },
    true
  );

  function fieldWrapperFor(input) {
    if (!input) return null;
    // offsetParent is null for a display:none input — the header field is
    // hidden behind a trigger on mobile, and anchoring to it would be as
    // wrong as anchoring to the far side of the page.
    if (!input.offsetParent && input.getClientRects().length === 0) return null;
    return input.closest(".plp-search-field, .bs-search") || input;
  }

  function activeSearchField() {
    var focused = document.activeElement;
    if (focused && focused.name === "q") {
      lastSearchInput = focused;
      return fieldWrapperFor(focused);
    }

    var remembered = fieldWrapperFor(lastSearchInput);
    if (remembered && lastSearchInput.isConnected) return remembered;

    // Nothing has been focused yet (the panel can open from a click on the
    // field's wrapper). Prefer the visible one rather than the first in the
    // document, since document order is what caused the bug above.
    var inputs = document.querySelectorAll('input[name="q"]');
    for (var i = 0; i < inputs.length; i++) {
      var wrapper = fieldWrapperFor(inputs[i]);
      if (wrapper) return wrapper;
    }
    return null;
  }

  function alignPopupToField() {
    var popup = document.querySelector(".usf-popup.usf-is");
    if (!popup) return;
    // Mobile gets Sobooster's own full-bleed treatment, which is already
    // edge-to-edge and has no field to anchor to.
    if (document.body.classList.contains("usf-mobile")) return;

    var field = activeSearchField();
    if (!field) return;

    var f = field.getBoundingClientRect();
    var width = popup.getBoundingClientRect().width;
    if (!width || !f.width) return;

    var viewport = document.documentElement.clientWidth;
    // Field past the halfway mark ⇒ it lives at the end edge of its row, so
    // the panel's end edge is the one that should agree with it.
    var left = f.left + f.width / 2 > viewport / 2 ? f.right - width : f.left;

    var max = viewport - PANEL_GUTTER - width;
    if (left > max) left = max;
    if (left < PANEL_GUTTER) left = PANEL_GUTTER;
    left = Math.round(left);

    /* Corrected by the REMAINING DELTA rather than by assigning the target
       straight to style.left.

       Two bugs live here, both measured. Remembering our own last write and
       skipping when it matched meant Sobooster could reposition the panel
       afterwards and we would never correct it (RTL: their 126px stuck while
       the field sat at 24px). Comparing the live rect instead fixed that but
       introduced a worse one: getBoundingClientRect().left is a VIEWPORT
       coordinate while style.left is resolved against the offsetParent, so
       wherever those two origins differ the comparison can never be
       satisfied — every pass wrote, every write raised a mutation, and the
       observer called us straight back. Measured at 585 mutations in 2.5s
       against 0 at HEAD, with the panel's offsetParent no longer at the
       viewport origin once a scope was active.

       Moving by the delta is origin-agnostic: whatever space style.left is
       resolved in, adding the gap between where the panel is and where it
       should be converges in one step. The 1px tolerance stops sub-pixel
       layout from re-triggering it forever. */
    var currentLeft = popup.getBoundingClientRect().left;
    var delta = left - currentLeft;
    if (Math.abs(delta) >= 1) {
      var styleLeft = parseFloat(popup.style.left);
      if (isNaN(styleLeft)) styleLeft = currentLeft;
      popup.style.left = Math.round(styleLeft + delta) + "px";
    }

    pointCaretAtField(popup, f, width);
  }

  /* The panel's little pointer is .usf-popup::before — a 16px square rotated
     45°. Sobooster pins it a fixed 50px from one edge of the panel
     (usf.css: right:50px, with .usf-is--left and rtl.css flipping which
     edge), which only points at anything while the panel hugs the field.
     It doesn't here: the panel is 858px wide and anchored to the field's
     near edge, so the fixed inset lands inside the field in LTR by luck and
     misses entirely in RTL — measured on /ar/search, caret at x≈816 with
     the field spanning 24→644. That is the stray white square: a pointer
     aimed at empty page, reading as a floating diamond rather than a tail.

     Pointed at the field's centre instead, in the same pass that positions
     the panel, so the two can't disagree. Hidden outright when the field's
     centre falls outside the panel — better no tail than one aimed wrong. */
  var CARET_HALF = 8;
  var CARET_EDGE_MIN = 18; // keeps it clear of the panel's rounded corners

  /* Every write here is guarded on the value actually differing, including
     the class toggles. classList.add/remove writes the class attribute even
     when the class list is unchanged, and an attribute write raises a
     MutationObserver record regardless of whether the value differs — so an
     unguarded remove() on each pass fed the observer that calls this
     straight back into it, and the panel never settled long enough for its
     own left to be re-applied. Measured symptom: the panel sat at
     Sobooster's 126px in RTL with our 24px never sticking. */
  function setCaretHidden(popup, hidden) {
    if (popup.classList.contains("bs-caret-hidden") === hidden) return;
    popup.classList.toggle("bs-caret-hidden", hidden);
  }

  function pointCaretAtField(popup, fieldRect, panelWidth) {
    // Measured off where the panel actually IS, not where we asked it to be:
    // Sobooster can reposition it after our write, and an offset based on the
    // intended left then points at the wrong place by exactly that drift.
    var panelLeft = popup.getBoundingClientRect().left;
    var centre = fieldRect.left + fieldRect.width / 2;
    var offset = Math.round(centre - panelLeft - CARET_HALF);
    var max = panelWidth - CARET_EDGE_MIN - CARET_HALF * 2;

    if (offset < CARET_EDGE_MIN || offset > max) {
      setCaretHidden(popup, true);
      return;
    }
    setCaretHidden(popup, false);
    if (popup.style.getPropertyValue("--bs-caret-left") === offset + "px") return;
    popup.style.setProperty("--bs-caret-left", offset + "px");
  }

  window.addEventListener("resize", alignPopupToField);

  /* ── Trending searches ───────────────────────────────────────────────
     The panel's side column is largely empty on the default screen (recent
     searches only, and nothing at all for a first-time visitor), and empty
     under a query once suggestions run short. Trending fills it with the
     thing shoppers most often want from a search box they have not typed
     into yet.

     Deliberately deferred to the app: Sobooster's own template renders both
     a live popular-search block (result.popularSearch) and a merchant list
     (.usf-is-suggestions--manual), either of which is real search-log data
     and therefore better than a hand-maintained list. Neither is populated
     on this store today — verified against the live endpoint, which returns
     popularSearch: null for every query including the empty one, and
     against settings.instantSearch, which carries no manualSuggestions — so
     ours renders now and stands down the moment theirs appears.

     Clicks route through scopedSearchUrl(), the same destination "view all
     results" and Enter already use, so a trending term obeys the active
     Women/Men/Kids tab exactly as typing it would. Nothing here touches the
     query path or the app's own request. */
  var TRENDING = window.BS_TRENDING || {};

  // The panel has a fixed max-height and already scrolls when the product
  // column is full; the full ten-term list pushed the side column past it and
  // put a scrollbar inside the dropdown. Six keeps the block inside the
  // panel's own height next to a full six-product result. The search page's
  // chip row is not capped — a horizontal rail has room for all of them.
  var TRENDING_IN_PANEL = 6;

  function trendingTerms() {
    if (!TRENDING.enabled) return [];
    return (TRENDING.terms || [])
      .filter(function (t) {
        return t && String(t).trim();
      })
      .slice(0, TRENDING_IN_PANEL);
  }

  function buildTrendingBlock(terms) {
    var wrap = document.createElement("div");
    // Carries Sobooster's own block classes as well as ours so it inherits
    // their column spacing for free and cannot drift from the neighbouring
    // recent-searches block when they change it.
    wrap.className = "usf-is-matches usf-is-suggestions bs-trending";

    var title = document.createElement("div");
    title.className = "usf-title bs-trending-title";
    title.textContent = I18N.trending_searches || I18N.trending || "Trending searches";
    wrap.appendChild(title);

    var list = document.createElement("div");
    list.className = "bs-trending-list";

    terms.forEach(function (term, i) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "usf-btn bs-trending-item";
      // Rank is decoration, so it is a pseudo-element fed by an attribute
      // rather than text a screen reader would read out before the term.
      btn.setAttribute("data-bs-rank", String(i + 1));
      btn.textContent = term;
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = scopedSearchUrl(term);
      });
      list.appendChild(btn);
    });

    wrap.appendChild(list);
    return wrap;
  }

  /* The side column only exists when Sobooster has something of its own to
     put in it — its template gates the whole element on `v-if
     !isEmptyExtraData`, and on the default screen (nothing typed, no recent
     searches yet, no manual list configured) that is false, so the panel
     renders one full-width products column and no second column at all.
     That is exactly the state trending is most wanted in, so the column has
     to be created rather than found.

     The class is what drives their layout, not inline widths — read out of
     usf.css directly:
       .usf-is-content:not(--without-sidebar):not(--without-results) { display:flex }
         └ .usf-is-products { width:66.66% }  .usf-is-side { width:33.33% }
       .usf-is--compact .usf-is-content { display:block }   ← the fallback
     So dropping --without-sidebar hands the panel straight back to their own
     two-column rules and the injected column picks up the same third of the
     width, the same divider and the same scroll behaviour as the real one.
     Nothing here restyles their layout; it just stops claiming there is no
     sidebar once there is one.

     Vue re-adds the class whenever it re-renders. The observer that calls
     this simply removes it again, and the pass that follows our own edit
     finds nothing left to change, so it settles instead of looping. */
  var NO_SIDEBAR_CLASS = "usf-is-content--without-sidebar";

  function trendingSideColumn(popup) {
    // Sobooster's own column comes first in the DOM when it exists (ours is
    // appended), so this prefers theirs and the caller drops our redundant
    // one — the panel never shows two.
    var existing = popup.querySelector(".usf-is-side");
    if (existing) return existing;

    var content = popup.querySelector(".usf-is-content");
    if (!content) return null;

    var side = document.createElement("div");
    side.className = "usf-is-side bs-trending-side";

    // On mobile their content is display:block (.usf-is--compact
    // .usf-is-content), so the two columns stack instead of sitting side by
    // side and `order` has nothing to act on — position has to come from the
    // DOM. Appended, the block landed under six full-width product rows,
    // far below the fold on a 375px screen. Ahead of the products it is the
    // first thing in a panel the shopper has not typed into yet, which is
    // the whole point of it. Desktop keeps it in the second column.
    if (document.body.classList.contains("usf-mobile")) {
      var products = content.querySelector(".usf-is-products");
      if (products) {
        content.insertBefore(side, products);
        return side;
      }
    }

    content.appendChild(side);
    return side;
  }

  function maybeInjectTrending() {
    var terms = trendingTerms();
    if (!terms.length) return;

    document.querySelectorAll(".usf-popup.usf-is").forEach(function (popup) {
      // Their column, when present, is the one to fill; otherwise ours.
      var side = trendingSideColumn(popup);
      if (!side) return;

      // A real column of theirs makes our synthetic one redundant.
      var synthetic = popup.querySelector(".bs-trending-side");
      if (synthetic && synthetic !== side) synthetic.remove();

      var content = popup.querySelector(".usf-is-content");
      if (content && side.classList.contains("bs-trending-side")) {
        content.classList.remove(NO_SIDEBAR_CLASS);
      }

      // Stand down while the app is serving its own — either block is real
      // search-log data and outranks our list.
      var appOwn = side.querySelector(".usf-is-suggestions--manual");
      var existing = side.querySelector(".bs-trending");
      if (appOwn) {
        if (existing) existing.remove();
        return;
      }
      // Vue rebuilds this column on every keystroke, so presence has to be
      // re-checked rather than assumed from a one-time insert (the same
      // reason ensureTabsInjected re-inserts the tab bar).
      if (existing) return;

      side.appendChild(buildTrendingBlock(terms));
    });
  }

  /* The search page's trending chips are Liquid-rendered links (so they
     survive JS being off), which means their href cannot know about a scope
     chosen client-side. Rewritten through the same scopedSearchUrl() the
     dropdown's block uses, so a chip clicked under the Women tab lands on
     Women results — the two surfaces behave identically. Left alone on All,
     where the plain href is already correct, which is also what keeps
     middle-click and "open in new tab" working in the common case. */
  document.addEventListener(
    "click",
    function (e) {
      if (!SCOPE_URL_VALUES[getScope()]) return;
      if (!e.target || !e.target.closest) return;
      // Modified clicks belong to the browser, not to us.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      var chip = e.target.closest(".plp-trending-item");
      if (!chip) return;
      var term = chip.dataset.bsTrendingTerm || chip.textContent.trim();
      if (!term) return;
      e.preventDefault();
      window.location.href = scopedSearchUrl(term);
    },
    true
  );

  /* ── Keep the "you are here" term on the real query ──────────────────
     The search page marks whichever trending term matches the current query
     (.is-current, set in sections/main-usf-search.liquid). That is correct
     for a server-rendered load and wrong from then on: Sobooster runs with
     canChangeUrl on (verified: usf.settings.search.canChangeUrl === 1), so
     searching from the field rewrites the URL, the title, the count and the
     whole grid client-side without the section ever re-rendering. The class
     therefore stayed frozen on the term the HTML was first built with —
     search "aigner" from a page opened on "creed" and the row still pointed
     at Creed while the title read Aigner.

     The URL's q is the authority: it is what the grid is actually showing,
     and Sobooster keeps it in step. The Liquid version stays as-is so the
     first paint is right with no JS and before this runs.

     Runs from the same mutation pass as everything else, so the grid's own
     re-render is what triggers it. Writes only on an actual change, which is
     what stops our own class edit from feeding the observer back into here. */
  function currentQueryTerm() {
    try {
      var q = new URLSearchParams(window.location.search).get("q");
      return q ? q.trim().toLowerCase() : "";
    } catch (e) {
      return "";
    }
  }

  function syncTrendingCurrent() {
    var chips = document.querySelectorAll(".plp-trending-item");
    if (!chips.length) return;
    var q = currentQueryTerm();

    chips.forEach(function (chip) {
      var term = (chip.dataset.bsTrendingTerm || chip.textContent || "").trim().toLowerCase();
      var shouldBeCurrent = !!q && term === q;
      if (chip.classList.contains("is-current") === shouldBeCurrent) return;
      chip.classList.toggle("is-current", shouldBeCurrent);
      if (shouldBeCurrent) {
        chip.setAttribute("aria-current", "true");
      } else {
        chip.removeAttribute("aria-current");
      }
    });
  }

  // Back/forward moves between queries without a mutation of its own.
  window.addEventListener("popstate", syncTrendingCurrent);

  function onDomMutated() {
    ensureTabsInjected();
    guardAgainstStaleRepaint();
    decorateViewAll();
    maybeInjectTrending();
    alignPopupToField();
    syncTrendingCurrent();
  }

  var observer = new MutationObserver(onDomMutated);
  observer.observe(document.body, { childList: true, subtree: true });
  onDomMutated();
})();
